import React, { useState, useEffect, useMemo, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import arTranslations from '../translations/receipt/ar.json';
import enTranslations from '../translations/receipt/en.json';
//functions
import {
  getSystemSettings,
} from "../functions/Functions";

import { SettingsContext } from '../contexts/Settings';

export const useReceiptTranslation = () => {
  const { t } = useTranslation();
  const { generalSettings } = useContext(SettingsContext);
  // const [isArabicEnabled, setIsArabicEnabled] = useState(true);
  const [receiptTranslations, setReceiptTranslations] = useState(enTranslations);

  const isArabicEnabled = useMemo(() => {
    return parseInt(getSystemSettings(generalSettings, "is_arabic_enable")) === 1;
  }, [generalSettings]);

  useEffect(() => {
    // Load Arabic translations when enabled
    if (isArabicEnabled) {
      setReceiptTranslations(arTranslations);
    } else {
      setReceiptTranslations(enTranslations);
    }
  }, [isArabicEnabled]);

  // Function to get dual language text (English + Arabic if enabled)
  const getDualText = (key, fallbackKey = null) => {
    const englishText = t(fallbackKey || key);
    const arabicText = receiptTranslations[key] || englishText;
    
    if (isArabicEnabled) {
      return (
        <>
          <span className="d-block">{englishText}</span>
          <span className="d-block" style={{marginTop: "-0.05rem"}}>{arabicText}</span>
        </>
      );
    }
    
    return englishText;
  };

  const getDualTextKey = (key, fallbackKey = null) => {
    const englishText = t(key);
    const arabicText = receiptTranslations[key] || englishText;
    if (isArabicEnabled) {
      return (
        <>
          <span className="d-inline-block">{englishText}{fallbackKey}</span>
          <span className="d-inline-block ml-1" style={{marginTop: "-0.05rem"}}>{arabicText}</span>
        </>
      );
    }
    
    return (englishText + fallbackKey);
  };

  const getCombination = (key1, key2, fallbackKey = null) => {
    const english1 = t(fallbackKey || key1);
    const arabic1 = key1 ? (receiptTranslations[key1.toLowerCase()] || english1) : null;
  
    const english2 = key2 ? t(key2) : null;
    const arabic2 = key2 ? (receiptTranslations[key2.toLowerCase()] || english2) : null;
  
    if (isArabicEnabled) {
      return (
        <>
          {/* English */}
          <span className="d-block">
            {english1}{english2 ? ` - ${english2}` : ""}
          </span>
  
          {/* Arabic */}
          <span className="d-block" style={{marginTop: "0.05rem"}}>
            {arabic1}{arabic2 ? ` - ${arabic2}` : ""}
          </span>
        </>
      );
    }
    return (english1 + (english2 ? ` - ${english2}` : ""));
  }

  const getDualTextValue = (key, value = "", split = null) => {
    const englishText = t(key);
    const arabicText = receiptTranslations[key] || englishText;
    
    if (isArabicEnabled) {
      return (
        <>
          <span className="d-inline-block">{englishText}</span>
          {split ?? "" }
          {value && <span className='d-inline-block'>{value}</span>}
          <span className="d-inline-block ml-1" style={{marginTop: "-0.15rem"}}>{arabicText}</span>
        </>
      );
    }
    return englishText + (split ?? "") + (value && value);
  };

  const getTranlatedValue = (value1 , value2 = "", sameLine= false) => {
    return (
      <>
        <span className={`${sameLine ? "d-inline-block" : "d-block"}`}>{value1}</span>
        {value2 && <span className={`${sameLine ? "d-inline-block" : "d-block"}`}>{value2}</span>}
      </>
    );
  };

  const renderItemName = (item, kds= false, dSettle= true)=>{
    const allowComments = parseInt(getSystemSettings(generalSettings, "customer_print_instruction")) === 1;
    if(dSettle){
      const showVariation = parseInt(item.item.has_variation) === 1;
      let text1 = item.item.name;
      let text2 = item.item?.translated_name ?? "";
  
      if (showVariation && item.variation) {
        text1 += ` (${item.variation.variation_name})`;
        text2 += item.variation?.variation_translated_name ? ` (${item.variation?.variation_translated_name ?? ""})` : "";
      }
  
      if ((allowComments || kds) && item.comments) {
        text1 += ` (${item.comments})`;
      }
      return getTranlatedValue(text1,text2)    
    }else{
      let text1 = item.food_item;
      let text2 = item?.food_items?.translated_name ?? "";
      if(item.variation){
        text1 += ` (${item.variation})`;
        text2 += item?.foodvariation?.translated_name ? ` (${item.foodvariation?.translated_name ?? ""})` : "";
      }
      if((allowComments || kds) && item?.comments){
        text1 += ` (${item.comments})`;
      }
      return getTranlatedValue(text1,text2)
    }
  }


  const renderOnlineItemName = (item, kds= false,)=>{
    const allowComments = parseInt(getSystemSettings(generalSettings, "customer_print_instruction")) === 1;
    let text1 = item.food_item;
    let text2 = item?.food_items?.translated_name ?? "";
    if(item.variation){
      text1 += ` (${item.variation})`;
      text2 += item?.foodvariation?.translated_name ? ` (${item.foodvariation?.translated_name ?? ""})` : "";
    }
    if((allowComments || kds) && item?.order_notes){
      text1 += ` (${item.order_notes})`;
    }
    return getTranlatedValue(text1,text2)
  }

  // Function to get only Arabic text (for specific cases)
  const getArabicText = (key) => {
    return receiptTranslations[key] || t(key);
  };

  // Function to get only English text
  const getEnglishText = (key) => {
    return t(key);
  };

  return {
    isArabicEnabled,
    getCombination,
    getTranlatedValue,
    renderItemName,
    renderOnlineItemName,
    getDualTextKey,
    getDualTextValue,
    getDualText,
    getArabicText,
    getEnglishText,
  };
};
